import React, { Fragment, useEffect, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';

import {
  FIELD_FILLING_TYPES,
  getFieldProps,
} from '../../../../utils/fields/getFieldProps';
import { PROFILE_FIELDS_TYPES } from '../../../../constants/formFields';
import {
  adaptInitialValueCountry,
  adaptInitialValueState,
} from '../../../ProfileLayout/HomeAddress/utils';
import { FormErrorBlock } from '../../../common/FormErrorBlock';
import SelectCustom from '../../../SelectCustom';
import ErrorBlock from '../../../common/ErrorBlock';
import TextInput from '../../../TextInput';
import { useLoggedUserCountry } from '../../utils/useLoggedUserCountry';
import styles from '../../styles.styl';

const {
  ADDRESS_1,
  ADDRESS_2,
  CITY,
  STATE,
  POSTAL_CODE,
  COUNTRY,
} = PROFILE_FIELDS_TYPES;

const GeneralInfoAddressForm = ({
  postalCode,
  address1,
  address2,
  city,
  state,
  country,
  validateErrors,
  formatMessage,
  onChange,
  statesList,
  countryList,
  zipLimit,
  onCountryChange,
  isZipApplicable,
  zipExamples,
  loadCountryDetails,
}) => {
  const makeProfileField = getFieldProps(formatMessage, onChange);
  const targetCountryEntity = useLoggedUserCountry();

  const handleCountryChange = useMemo(
    () => data => {
      if (!data) return;
      if (country === data || country === data.label) {
        onCountryChange(data);
        return;
      }
      onCountryChange(data);
    },
    [country, targetCountryEntity, address1, city],
  );

  const safeCountry = adaptInitialValueCountry(country, countryList);
  const safeState = adaptInitialValueState(state, statesList);

  useEffect(() => {
    if (targetCountryEntity) loadCountryDetails(targetCountryEntity.value);
    if (country) {
      const countryEntity = adaptInitialValueCountry(
        country.label,
        countryList,
      );
      loadCountryDetails(countryEntity.value);
    }
  }, [targetCountryEntity, country]);

  return (
    <Fragment>
      <div className={styles.column}>
        {/*     ADDRESS_1    */}
        <div>
          <TextInput
            {...makeProfileField(ADDRESS_1, FIELD_FILLING_TYPES.REQUIRED)}
            value={address1}
            required={false}
          />
          <FormErrorBlock type={ADDRESS_1} errors={validateErrors} />
        </div>

        {/*     ADDRESS_2    */}
        <div>
          <TextInput
            {...makeProfileField(ADDRESS_2, FIELD_FILLING_TYPES.OPTIONAL)}
            value={address2}
            required={false}
          />
          <FormErrorBlock type={ADDRESS_2} errors={validateErrors} />
        </div>
      </div>

      <div className={styles.column}>
        {/*     CITY     */}
        <div>
          <TextInput
            {...makeProfileField(CITY, FIELD_FILLING_TYPES.REQUIRED)}
            required={false}
            value={city}
          />
          <FormErrorBlock type={CITY} errors={validateErrors} />
        </div>

        {/*     STATE     */}
        <div>
          <SelectCustom
            {...makeProfileField(STATE, FIELD_FILLING_TYPES.REQUIRED)}
            onChange={data => onChange({ state: data })}
            value={safeState}
            options={statesList}
            needUpdateDefaultValue
            required={false}
          />
          <FormErrorBlock type={STATE} errors={validateErrors} />
        </div>
      </div>

      <div className={styles.column}>
        {/*     POSTAL CODE   */}
        <div>
          <TextInput
            {...makeProfileField(POSTAL_CODE, FIELD_FILLING_TYPES.REQUIRED)}
            limit={zipLimit}
            value={postalCode}
            disabled={!isZipApplicable}
            shouldUpperCase
            required={false}
          />
          <If condition={get(validateErrors, POSTAL_CODE)}>
            <ErrorBlock
              errors={[
                formatMessage(
                  { id: 'errors.validation.profile.postalCode.format' },
                  { example: zipExamples },
                ),
              ]}
              className="mt3 w-100"
            />
          </If>
        </div>

        {/*     COUNTRY     */}
        <div>
          <SelectCustom
            {...makeProfileField(COUNTRY, FIELD_FILLING_TYPES.REQUIRED)}
            onChange={val => handleCountryChange(val)}
            value={safeCountry || targetCountryEntity}
            options={countryList}
            needUpdateDefaultValue
            required={false}
          />
          <FormErrorBlock type={COUNTRY} errors={validateErrors} />
        </div>
      </div>
    </Fragment>
  );
};

GeneralInfoAddressForm.propTypes = {
  formatMessage: PropTypes.func,
  postalCode: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  state: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  validateErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  address1: PropTypes.string,
  address2: PropTypes.string,
  phoneNumber: PropTypes.string,
  onChange: PropTypes.func,
  statesList: PropTypes.array,
  countryList: PropTypes.array,
  zipLimit: PropTypes.number,
  onCountryChange: PropTypes.func,
  isZipApplicable: PropTypes.bool,
  zipExamples: PropTypes.string,
  loadCountryDetails: PropTypes.func,
};

export default GeneralInfoAddressForm;



// WEBPACK FOOTER //
// ./src/components/DependentForm/DependentGeneralInfo/GeneralInfoAddressForm/index.jsx